import { ReactNode } from "react";
import { Tag } from "@carbon/react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import FileReader from "../../components/FileReader/FileReader";
import { MaterialOfferResponse, SampleEnum } from "../../types";
import { IT_DOESNT_HAVE_SPEC } from "../../utils";

type TFunc = (key: string) => string;

/**
 * Sample state as a Carbon `Tag` — same "status as a colour-coded chip" convention
 * `materialRows.tsx#statusTag` and `BiddingOpportunities/biddingTypeTag.tsx` use, replacing the
 * pre-Carbon `MaterialOfferTableRow`'s four independent `isInitialized`/`isRequested`/
 * `isRejected`/`isVerified` booleans (mutually exclusive in practice, since `state` is one value)
 * with a single switch. `INITIALIZED` (and any unrecognised state) falls back to plain "N/A" text,
 * same as the pre-Carbon row's own `isInitialized` branch.
 */
const sampleTag = (state: SampleEnum, t: TFunc): ReactNode => {
  switch (state) {
    case SampleEnum.REQUESTED:
      return (
        <Tag type="cyan" size="sm">
          {t("sampleStates.requested")}
        </Tag>
      );
    case SampleEnum.REJECTED:
      return (
        <Tag type="red" size="sm">
          {t("sampleStates.rejected")}
        </Tag>
      );
    case SampleEnum.VERIFIED:
      return (
        <Tag type="green" size="sm">
          {t("sampleStates.verified")}
        </Tag>
      );
    default:
      return "N/A";
  }
};

const specCell = (offer: MaterialOfferResponse): ReactNode =>
  offer.specificationFileUrl ? (
    <FileReader fileKey={offer.specificationFileUrl} />
  ) : (
    <span>{IT_DOESNT_HAVE_SPEC}</span>
  );

export interface MaterialOfferRow extends DataTableRow {
  id: string;
  material: string;
  origin: string;
  sample: ReactNode;
  spec: ReactNode;
  responsible?: string;
}

/**
 * `isCompany` mirrors the pre-Carbon `MaterialOfferTableRow`'s own prop: the "All Company
 * Applications" table adds a trailing `responsible` (`offer.user.fullName`) column the caller's
 * own "My Applications" table doesn't have — see `materialOfferHeaders` below for the matching
 * conditional column.
 */
export const buildMaterialOfferRows = (
  offers: MaterialOfferResponse[],
  isCompany: boolean,
  t: TFunc
): MaterialOfferRow[] =>
  offers.map((offer) => ({
    id: offer.id,
    material: offer.material?.globalMaterialName || "-",
    origin: offer.origin?.name || "-",
    sample: sampleTag(offer.state, t),
    spec: specCell(offer),
    ...(isCompany ? { responsible: offer.user?.fullName || "-" } : {}),
  }));

export const materialOfferHeaders = (
  t: TFunc,
  isCompany: boolean
): DataTableColumn<MaterialOfferRow>[] => {
  const headers: DataTableColumn<MaterialOfferRow>[] = [
    { key: "material", header: t("offer.material") },
    { key: "origin", header: t("offer.origin") },
    { key: "sample", header: t("offer.sample") },
    { key: "spec", header: t("offer.spec") },
  ];
  if (isCompany) {
    headers.push({ key: "responsible", header: t("users.responsible") });
  }
  return headers;
};

/** `/material/offer/:id` (`MaterialOffer.tsx`) — unchanged destination, same as the pre-Carbon
 * `Link`-wrapped name cell + trailing chevron link. */
export const navigateToMaterialOffer = (
  navigate: (path: string) => void,
  row: MaterialOfferRow
): void => navigate(`/material/offer/${row.id}`);
